import React from 'react';
import {
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  AccountTree as ScopeIcon,
  Check as CompleteIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useClipboard } from '../../../shared/hooks/useClipboard';
import type { InvestigationSummary } from '../../../types';

interface InvestigationContextMenuProps {
  anchorEl: HTMLElement | null;
  investigation: InvestigationSummary | undefined;
  onClose: () => void;
  onManageScope: () => void;
  onDelete: () => void;
  onUpdateStatus: (state: 'active' | 'completed' | 'archived') => void;
}

const InvestigationContextMenu: React.FC<InvestigationContextMenuProps> = ({
  anchorEl,
  investigation,
  onClose,
  onManageScope,
  onDelete,
  onUpdateStatus,
}) => {
  const currentState = investigation?.state || 'active';
  const { copy } = useClipboard();

  const handleCopyLink = () => {
    if (!investigation) return;
    const url = `${window.location.origin}/customers/${investigation.customer_id}?investigation=${investigation.id}`;
    copy(url);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      <MenuItem onClick={handleCopyLink} disabled={!investigation}>
        <LinkIcon sx={{ mr: 1, fontSize: 16 }} />
        Copy Link
      </MenuItem>
      <MenuItem onClick={onManageScope}>
        <ScopeIcon sx={{ mr: 1, fontSize: 16 }} />
        Manage Scope
      </MenuItem>
      <Divider />
      {currentState === 'active' && [
        <Tooltip key="complete" title="Use the chat to complete investigations with a resolution summary" placement="left">
          <span>
            <MenuItem disabled sx={{ opacity: 0.6 }}>
              <CompleteIcon sx={{ mr: 1, fontSize: 16 }} />
              Mark as Completed
            </MenuItem>
          </span>
        </Tooltip>,
        <MenuItem key="archive" onClick={() => {
          onUpdateStatus('archived');
          onClose();
        }}>
          <ArchiveIcon sx={{ mr: 1, fontSize: 16 }} />
          Archive
        </MenuItem>,
      ]}
      {currentState === 'completed' && [
        <MenuItem key="reopen" onClick={() => {
          onUpdateStatus('active');
          onClose();
        }}>
          <RestoreIcon sx={{ mr: 1, fontSize: 16 }} />
          Reopen
        </MenuItem>,
        <MenuItem key="archive" onClick={() => {
          onUpdateStatus('archived');
          onClose();
        }}>
          <ArchiveIcon sx={{ mr: 1, fontSize: 16 }} />
          Archive
        </MenuItem>,
      ]}
      {currentState === 'archived' && (
        <MenuItem onClick={() => {
          onUpdateStatus('active');
          onClose();
        }}>
          <RestoreIcon sx={{ mr: 1, fontSize: 16 }} />
          Unarchive
        </MenuItem>
      )}
      <Divider />
      <MenuItem onClick={onDelete}>
        <DeleteIcon sx={{ mr: 1, fontSize: 16, color: 'error.main' }} />
        Delete Investigation
      </MenuItem>
    </Menu>
  );
};

export default InvestigationContextMenu;
